import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Alert from '@cloudscape-design/components/alert';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import GermanDatePicker from '../components/GermanDatePicker';
import api, { resolveBackendAssetUrl } from '../services/apiService';
import { getMachineCardSpecs } from '../utils/catalogSpecs';
import { germanDateToIso } from '../utils/date';

const emptyForm = {
  requesterName: '', company: '', email: '', phone: '', postalCode: '', city: '',
  country: 'DE', transportMode: '', streetAddress: '', startDate: '', endDate: '', weekendWorkMode: '', desiredOfferCount: '3', message: '',
  privacyAccepted: false, website: '',
};

const transportOptions = [
  { value: 'delivery_pickup', label: 'Anlieferung / Abholung' },
  { value: 'self_pickup', label: 'Selbstabholer' },
];

const trainingLanguageOptions = [
  { value: 'DE', label: 'Deutsch (DE)' },
  { value: 'EN', label: 'Englisch (EN)' },
  { value: 'PL', label: 'Polnisch (PL)' },
  { value: 'RO', label: 'Rumänisch (RO)' },
  { value: 'TR', label: 'Türkisch (TR)' },
];

const operatorTrainingStandards = {
  'arbeitsbühnen': { product: 'Arbeitsbühnen', code: 'DGUV 308-008' },
  'gabelstapler': { product: 'Gabelstapler', code: 'DGUV 308-001' },
  'teleskopstapler': { product: 'Teleskopstapler', code: 'DGUV 308-009' },
};

function createRequestItem(selection = '') {
  return {
    selection,
    quantity: '1',
    details: '',
    operatorTrainingRequested: false,
    operatorTrainingGroups: [{ participantCount: '1', language: '' }],
  };
}

const weekendWorkOptions = [
  { value: 'none', label: 'Nein' },
  { value: 'saturday', label: 'Samstag' },
  { value: 'sunday', label: 'Sonntag' },
  { value: 'both', label: 'Samstag und Sonntag' },
];

function calculateRentalDays(startValue, endValue, weekendWorkMode) {
  const startDate = germanDateToIso(startValue);
  const endDate = germanDateToIso(endValue);
  if (!startDate || !endDate || !weekendWorkMode || endDate < startDate) return null;
  const startMs = Date.parse(`${startDate}T00:00:00.000Z`);
  const endMs = Date.parse(`${endDate}T00:00:00.000Z`);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  if (new Date(startMs).toISOString().slice(0, 10) !== startDate) return null;
  if (new Date(endMs).toISOString().slice(0, 10) !== endDate) return null;
  const dayMs = 86_400_000;
  const totalCalendarDays = Math.floor((endMs - startMs) / dayMs) + 1;
  const includeSaturday = ['saturday', 'both'].includes(weekendWorkMode);
  const includeSunday = ['sunday', 'both'].includes(weekendWorkMode);
  const fullWeeks = Math.floor(totalCalendarDays / 7);
  let days = fullWeeks * (5 + Number(includeSaturday) + Number(includeSunday));
  for (let offset = 0; offset < totalCalendarDays % 7; offset += 1) {
    const weekday = new Date(startMs + offset * dayMs).getUTCDay();
    if (weekday >= 1 && weekday <= 5) days += 1;
    if (weekday === 6 && includeSaturday) days += 1;
    if (weekday === 0 && includeSunday) days += 1;
  }
  return days;
}

function sortedSpecs(machine, limit) {
  const values = Array.isArray(machine?.specs) ? [...machine.specs] : [];
  const sorted = values.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0));
  return typeof limit === 'number' ? sorted.slice(0, limit) : sorted;
}

function MachinePreview({ machine, onDetails }) {
  const specs = getMachineCardSpecs(machine);
  const imageUrl = resolveBackendAssetUrl(machine.imageUrl);
  return (
    <div className="request-machine-preview">
      {imageUrl
        ? <img src={imageUrl} alt={machine.title} />
        : <div className="request-machine-image-placeholder">SR</div>}
      <div className="request-machine-summary">
        <span className="request-machine-path">
          {machine.category?.familyName || 'Maschinen'} · {machine.category?.name || 'Produktgruppe'} · {machine.model}
        </span>
        <h3>{machine.title}</h3>
        <p>{machine.manufacturer} {machine.model}</p>
        {specs.length > 0 && (
          <dl className="request-machine-specs">
            {specs.map((spec) => (
              <div key={spec.key}><dt>{spec.label}</dt><dd>{spec.value}{spec.unit ? ` ${spec.unit}` : ''}</dd></div>
            ))}
          </dl>
        )}
        <Button variant="link" onClick={() => onDetails(machine)}>Alle Maschinendetails ansehen</Button>
      </div>
    </div>
  );
}

export default function RequestPage() {
  const [searchParams] = useSearchParams();
  const preselectedMachineId = searchParams.get('maschine');
  const [form, setForm] = useState(emptyForm);
  const [machines, setMachines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([createRequestItem(preselectedMachineId ? `machine:${preselectedMachineId}` : '')]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [detailMachine, setDetailMachine] = useState(null);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityLookupLoading, setCityLookupLoading] = useState(false);
  const [citySuggestionSource, setCitySuggestionSource] = useState(null);
  const lastSuggestedCity = useRef('');

  useEffect(() => {
    Promise.all([api.get('/catalog/machines'), api.get('/catalog/categories')])
      .then(([machineResponse, categoryResponse]) => {
        setMachines(machineResponse.data);
        setCategories(categoryResponse.data);
      })
      .catch(() => setError('Maschinen und Kategorien konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const postalCode = form.postalCode.trim();
    setCitySuggestions([]);
    setCitySuggestionSource(null);
    setForm((current) => {
      if (lastSuggestedCity.current && current.city === lastSuggestedCity.current) {
        return { ...current, city: '' };
      }
      return current;
    });
    lastSuggestedCity.current = '';

    if (form.country !== 'DE' || !/^\d{5}$/.test(postalCode)) {
      setCityLookupLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setCityLookupLoading(true);
      api.get('/requests/location-suggestions', {
        params: { postalCode, country: form.country },
        signal: controller.signal,
      }).then((response) => {
        if (controller.signal.aborted) return;
        const suggestions = (response.data?.suggestions || []).map((suggestion) => ({
          value: suggestion.city,
          label: suggestion.city,
          description: suggestion.label,
        })).filter((suggestion) => suggestion.value);
        setCitySuggestions(suggestions);
        setCitySuggestionSource(suggestions.length ? response.data?.source : null);
        if (suggestions.length === 1) {
          setForm((current) => {
            if (current.postalCode.trim() !== postalCode || current.city.trim()) return current;
            lastSuggestedCity.current = suggestions[0].value;
            return { ...current, city: suggestions[0].value };
          });
        }
      }).catch(() => {
        if (controller.signal.aborted) return;
        setCitySuggestions([]);
        setCitySuggestionSource(null);
      }).finally(() => {
        if (!controller.signal.aborted) setCityLookupLoading(false);
      });
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.postalCode, form.country]);

  const options = useMemo(() => [
    ...machines.map((machine) => {
      const height = getMachineCardSpecs(machine)[0];
      const heightValue = `${height.value}${height.unit ? ` ${height.unit}` : ''}`;
      return {
        value: `machine:${machine.id}`,
        label: `${machine.manufacturer} ${machine.model}`,
        description: `${machine.category?.name || 'Mietmaschine'} · ${height.label}: ${heightValue}`,
        iconUrl: resolveBackendAssetUrl(machine.imageUrl) || undefined,
        iconAlt: machine.imageUrl ? `Vorschaubild ${machine.title}` : undefined,
      };
    }),
    ...categories.map((category) => ({ value: `category:${category.id}`, label: `${category.name} – Typ noch offen`, description: category.familyName || 'Maschinentyp' })),
  ], [machines, categories]);
  const rentalDays = useMemo(
    () => calculateRentalDays(form.startDate, form.endDate, form.weekendWorkMode),
    [form.startDate, form.endDate, form.weekendWorkMode]
  );
  const trainingPositions = useMemo(() => items.map((item, index) => {
    const machine = item.selection.startsWith('machine:')
      ? machines.find((entry) => `machine:${entry.id}` === item.selection)
      : null;
    const category = item.selection.startsWith('category:')
      ? categories.find((entry) => `category:${entry.id}` === item.selection)
      : machine?.category;
    const familyName = String(category?.familyName || '').trim();
    const standard = operatorTrainingStandards[familyName.toLocaleLowerCase('de')];
    if (!standard) return null;
    return {
      index,
      item,
      familyName,
      standard,
      label: machine ? `${machine.manufacturer} ${machine.model}` : category.name,
    };
  }).filter(Boolean), [items, machines, categories]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateItem = (index, field, value) => setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));
  const updateItemFields = (index, changes) => setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...changes } : item));
  const updateItemSelection = (index, selection) => updateItemFields(index, {
    selection,
    operatorTrainingRequested: false,
    operatorTrainingGroups: [{ participantCount: '1', language: '' }],
  });
  const updateTrainingGroup = (itemIndex, groupIndex, field, value) => setItems((current) => current.map((item, currentItemIndex) => (
    currentItemIndex === itemIndex ? {
      ...item,
      operatorTrainingGroups: item.operatorTrainingGroups.map((group, currentGroupIndex) => (
        currentGroupIndex === groupIndex ? { ...group, [field]: value } : group
      )),
    } : item
  )));
  const addTrainingGroup = (itemIndex) => setItems((current) => current.map((item, currentItemIndex) => (
    currentItemIndex === itemIndex ? {
      ...item,
      operatorTrainingGroups: [...item.operatorTrainingGroups, { participantCount: '1', language: '' }],
    } : item
  )));
  const removeTrainingGroup = (itemIndex, groupIndex) => setItems((current) => current.map((item, currentItemIndex) => (
    currentItemIndex === itemIndex ? {
      ...item,
      operatorTrainingGroups: item.operatorTrainingGroups.filter((_, currentGroupIndex) => currentGroupIndex !== groupIndex),
    } : item
  )));

  async function submit(event) {
    event.preventDefault();
    setError('');
    if (items.some((item) => !item.selection)) {
      setError('Bitte für jede Position eine Maschine oder einen Maschinentyp auswählen.');
      return;
    }
    const startDate = germanDateToIso(form.startDate);
    const endDate = germanDateToIso(form.endDate);
    if (!startDate || !endDate || endDate < startDate) {
      setError('Bitte einen gültigen Mietzeitraum im Format TT.MM.JJJJ angeben.');
      return;
    }
    if (!form.transportMode) {
      setError('Bitte eine Transportart auswählen.');
      return;
    }
    if (form.transportMode === 'delivery_pickup' && !/\d/.test(form.streetAddress.trim())) {
      setError('Für Anlieferung / Abholung sind Straße und Hausnummer erforderlich.');
      return;
    }
    if (!form.weekendWorkMode) {
      setError('Bitte angeben, ob am Wochenende gearbeitet wird.');
      return;
    }
    if (!rentalDays) {
      setError('Im gewählten Zeitraum ergeben sich mit der Wochenendauswahl keine Miettage.');
      return;
    }
    for (const item of items.filter((entry) => entry.operatorTrainingRequested)) {
      if (!Array.isArray(item.operatorTrainingGroups) || item.operatorTrainingGroups.length < 1 || item.operatorTrainingGroups.length > 5) {
        setError('Bitte für jede Bedienerschulung mindestens eine und höchstens fünf Sprachgruppen angeben.');
        return;
      }
      const languages = item.operatorTrainingGroups.map((group) => group.language);
      if (languages.some((language) => !trainingLanguageOptions.some((option) => option.value === language))) {
        setError('Bitte für jede Sprachgruppe eine Sprache auswählen.');
        return;
      }
      if (new Set(languages).size !== languages.length) {
        setError('Jede Schulungssprache darf pro Maschinenposition nur einmal ausgewählt werden.');
        return;
      }
      if (item.operatorTrainingGroups.some((group) => (
        !Number.isInteger(Number(group.participantCount))
        || Number(group.participantCount) < 1
        || Number(group.participantCount) > 100
      ))) {
        setError('Bitte für jede Sprachgruppe 1 bis 100 Teilnehmer angeben.');
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        startDate,
        endDate,
        desiredOfferCount: Number(form.desiredOfferCount),
        items: items.map((item) => {
          const [kind, id] = item.selection.split(':');
          return {
            ...(kind === 'machine' ? { machineId: id } : { categoryId: id }),
            quantity: Number(item.quantity || 1),
            details: item.details,
            operatorTrainingRequested: item.operatorTrainingRequested,
            operatorTrainingGroups: item.operatorTrainingRequested
              ? item.operatorTrainingGroups.map((group) => ({
                participantCount: Number(group.participantCount),
                language: group.language,
              }))
              : null,
          };
        }),
      };
      const response = await api.post('/requests', payload);
      setResult(response.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Die Anfrage konnte nicht gesendet werden.');
    } finally {
      setSubmitting(false);
    }
  }

  async function resendVerification() {
    setResendingVerification(true);
    setResendMessage('');
    try {
      const response = await api.post('/requests/verification/resend', {
        reference: result.reference,
        email: form.email,
      });
      setResendMessage(response.data.message);
    } catch (requestError) {
      setResendMessage(requestError.response?.data?.error || 'Die Bestätigungs-E-Mail konnte nicht erneut angefordert werden.');
    } finally {
      setResendingVerification(false);
    }
  }

  if (result) {
    return (
      <div className="form-shell">
        <SpaceBetween size="m">
          <Alert
            type={result.verificationEmailSent ? 'info' : 'error'}
            header={result.verificationEmailSent ? `Anfrage ${result.reference}: E-Mail bestätigen` : 'Bestätigungs-E-Mail konnte nicht versendet werden'}
          >
            {result.verificationEmailSent
              ? `Wir haben einen Bestätigungslink an ${form.email} gesendet. Erst nach Ihrer Bestätigung wird die Anfrage an passende Vermieter weitergeleitet.`
              : `Ihre Anfrage ${result.reference} wurde vorgemerkt, aber die Bestätigungs-E-Mail konnte nicht versendet werden. Fordern Sie den Link erneut an.`}
          </Alert>
          {!result.verificationEmailSent && (
            <Button loading={resendingVerification} onClick={resendVerification}>
              Bestätigungs-E-Mail erneut senden
            </Button>
          )}
          {resendMessage && <Alert type="success">{resendMessage}</Alert>}
        </SpaceBetween>
      </div>
    );
  }

  return (
    <div className="form-shell">
      <Form
        header={<Header variant="h1" description="Beschreiben Sie Ihren Einsatz. SmartRent fragt gezielt passende Vermieter an.">Mietanfrage</Header>}
        errorText={error}
        actions={<Button variant="primary" loading={submitting} onClick={submit}>Anfrage verbindlich absenden</Button>}
      >
        <form onSubmit={submit}>
          <SpaceBetween size="l">
            <Container header={<Header variant="h2">1. Maschinenbedarf</Header>}>
              <SpaceBetween size="m">
                {items.map((item, index) => {
                  const selectedMachine = item.selection.startsWith('machine:')
                    ? machines.find((machine) => `machine:${machine.id}` === item.selection)
                    : null;
                  const selectedCategory = item.selection.startsWith('category:')
                    ? categories.find((category) => `category:${category.id}` === item.selection)
                    : null;
                  return (
                    <div className="request-item-panel" key={index}>
                      <ColumnLayout columns={3} variant="text-grid">
                        <FormField label={`Maschine oder Maschinentyp ${index + 1}`}>
                          <Select
                            disabled={loading}
                            placeholder={loading ? 'Katalog wird geladen …' : 'Bitte auswählen'}
                            options={options}
                            selectedOption={options.find((option) => option.value === item.selection) || null}
                            onChange={({ detail }) => updateItemSelection(index, detail.selectedOption.value)}
                            filteringType="auto"
                          />
                        </FormField>
                        <FormField label="Anzahl">
                          <Input type="number" value={item.quantity} onChange={({ detail }) => updateItem(index, 'quantity', detail.value)} />
                        </FormField>
                        <FormField label="Zusatzangaben" description="">
                          <Input value={item.details} onChange={({ detail }) => updateItem(index, 'details', detail.value)} />
                        </FormField>
                      </ColumnLayout>
                      {selectedMachine && <MachinePreview machine={selectedMachine} onDetails={setDetailMachine} />}
                      {selectedCategory && (
                        <div className="request-category-preview">
                          <div className="request-category-icon">?</div>
                          <div>
                            <span>{selectedCategory.familyName || 'Maschinenfamilie'}</span>
                            <strong>{selectedCategory.name}</strong>
                            <p>Sie fragen bewusst einen Maschinentyp ohne festes Modell an. Der Vermieter schlägt ein passendes Gerät vor.</p>
                          </div>
                        </div>
                      )}
                      {items.length > 1 && (
                        <div className="request-item-remove"><Button variant="link" onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}>Position entfernen</Button></div>
                      )}
                    </div>
                  );
                })}
                <Box><Button iconName="add-plus" onClick={() => setItems((current) => [...current, createRequestItem()])}>Weitere Maschine</Button></Box>
              </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">2. Einsatz</Header>}>
              <ColumnLayout columns={2} variant="text-grid">
                <FormField label="Mietbeginn" description="">
                  <GermanDatePicker
                    ariaLabel="Mietbeginn"
                    value={form.startDate}
                    onChange={(value) => setField('startDate', value)}
                  />
                </FormField>
                <FormField label="Mietende" description="">
                  <GermanDatePicker
                    ariaLabel="Mietende"
                    value={form.endDate}
                    onChange={(value) => setField('endDate', value)}
                  />
                </FormField>
                <FormField label="Arbeit am Wochenende">
                  <Select
                    placeholder="Bitte auswählen"
                    options={weekendWorkOptions}
                    selectedOption={weekendWorkOptions.find((option) => option.value === form.weekendWorkMode) || null}
                    onChange={({ detail }) => setField('weekendWorkMode', detail.selectedOption.value)}
                  />
                </FormField>
                <FormField label="Berechnete Miettage" description="Montag bis Freitag sowie ausgewählte Wochenendtage">
                  <Box variant="strong">{rentalDays === null ? '–' : `${rentalDays} Miettag${rentalDays === 1 ? '' : 'e'}`}</Box>
                </FormField>
                <FormField label="Postleitzahl">
                  <Input
                    value={form.postalCode}
                    onChange={({ detail }) => setField('postalCode', detail.value)}
                    placeholder="z. B. 38440"
                  />
                </FormField>
                <FormField label="Einsatzort">
                  <Autosuggest
                    value={form.city}
                    options={citySuggestions}
                    statusType={cityLookupLoading ? 'loading' : 'finished'}
                    loadingText="Ort wird ermittelt"
                    empty="Ort kann manuell eingetragen werden"
                    enteredTextLabel={(value) => `"${value}" verwenden`}
                    selectedAriaLabel="Ausgewählt"
                    placeholder="Ort wird nach Eingabe der PLZ vorgeschlagen"
                    onChange={({ detail }) => {
                      lastSuggestedCity.current = '';
                      setField('city', detail.value);
                    }}
                  />
                  {citySuggestionSource === 'google' && citySuggestions.length > 0 && (
                    <div className="google-maps-attribution">
                      Ortsvorschlag von <span translate="no">Google Maps</span>
                    </div>
                  )}
                </FormField>
                <FormField label="Gewünschte Anzahl Preisinformationen">
                  <Select
                    options={['1', '2', '3', '4', '5'].map((value) => ({ value, label: `${value} Preisinformation${value === '1' ? '' : 'en'}` }))}
                    selectedOption={{ value: form.desiredOfferCount, label: `${form.desiredOfferCount} Preisinformation${form.desiredOfferCount === '1' ? '' : 'en'}` }}
                    onChange={({ detail }) => setField('desiredOfferCount', detail.selectedOption.value)}
                  />
                </FormField>
              </ColumnLayout>
              <FormField label="Hinweise zum Einsatz"><Textarea value={form.message} onChange={({ detail }) => setField('message', detail.value)} /></FormField>
            </Container>

            <Container
              header={<Header variant="h2" description="Transport und optionale Bedienerschulungen auswählen">3. Zusatzleistungen</Header>}
            >
              <SpaceBetween size="l">
                <ColumnLayout columns={2} variant="text-grid">
                  <FormField label="Transport">
                    <Select
                      placeholder="Bitte auswählen"
                      options={transportOptions}
                      selectedOption={transportOptions.find((option) => option.value === form.transportMode) || null}
                      onChange={({ detail }) => setForm((current) => ({
                        ...current,
                        transportMode: detail.selectedOption.value,
                        streetAddress: detail.selectedOption.value === 'delivery_pickup' ? current.streetAddress : '',
                      }))}
                    />
                  </FormField>
                  {form.transportMode === 'delivery_pickup' && (
                    <FormField label="Straße und Hausnummer" description="Genaue Adresse für Anlieferung und spätere Abholung">
                      <Input
                        value={form.streetAddress}
                        onChange={({ detail }) => setField('streetAddress', detail.value)}
                        placeholder="z. B. Musterstraße 12"
                      />
                    </FormField>
                  )}
                </ColumnLayout>

                <div>
                  <Header
                    variant="h3"
                    description="Verfügbar für Arbeitsbühnen, Gabelstapler und Teleskopstapler"
                  >
                    Bedienerschulung nach DGUV
                  </Header>
                  {trainingPositions.length > 0 ? (
                    <div className="operator-training-list">
                      {trainingPositions.map(({ index, item, familyName, label, standard }) => (
                        <div className="operator-training-panel" key={`${item.selection}-${index}`}>
                          <Checkbox
                            checked={item.operatorTrainingRequested}
                            onChange={({ detail }) => updateItemFields(index, detail.checked ? {
                              operatorTrainingRequested: true,
                              operatorTrainingGroups: item.operatorTrainingGroups?.length
                                ? item.operatorTrainingGroups
                                : [{ participantCount: '1', language: '' }],
                            } : {
                              operatorTrainingRequested: false,
                              operatorTrainingGroups: [{ participantCount: '1', language: '' }],
                            })}
                          >
                            Bedienerschulung {standard.product} ({standard.code}) für {label}
                          </Checkbox>
                          <small>{familyName} · Maschinenposition {index + 1}</small>
                          {item.operatorTrainingRequested && (
                            <div className="operator-training-groups">
                              {item.operatorTrainingGroups.map((group, groupIndex) => {
                                const languagesUsedElsewhere = new Set(item.operatorTrainingGroups
                                  .filter((_, currentIndex) => currentIndex !== groupIndex)
                                  .map((entry) => entry.language));
                                const availableLanguages = trainingLanguageOptions.filter((option) => !languagesUsedElsewhere.has(option.value));
                                return (
                                  <div className="operator-training-fields" key={groupIndex}>
                                    <FormField label={`Teilnehmer Sprachgruppe ${groupIndex + 1}`} description="1 bis 100 Personen">
                                      <Input
                                        type="number"
                                        value={group.participantCount}
                                        onChange={({ detail }) => updateTrainingGroup(index, groupIndex, 'participantCount', detail.value)}
                                      />
                                    </FormField>
                                    <FormField label="Schulungssprache">
                                      <Select
                                        placeholder="Bitte auswählen"
                                        options={availableLanguages}
                                        selectedOption={trainingLanguageOptions.find((option) => option.value === group.language) || null}
                                        onChange={({ detail }) => updateTrainingGroup(index, groupIndex, 'language', detail.selectedOption.value)}
                                      />
                                    </FormField>
                                    {item.operatorTrainingGroups.length > 1 && (
                                      <div className="operator-training-remove">
                                        <Button variant="link" onClick={() => removeTrainingGroup(index, groupIndex)}>Sprachgruppe entfernen</Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {item.operatorTrainingGroups.length < trainingLanguageOptions.length && (
                                <Box>
                                  <Button iconName="add-plus" onClick={() => addTrainingGroup(index)}>Weitere Sprache</Button>
                                </Box>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert type="info">
                      Wählen Sie eine Arbeitsbühne, einen Gabelstapler oder einen Teleskopstapler aus, um eine Bedienerschulung anzufragen.
                    </Alert>
                  )}
                </div>
              </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">4. Kontaktdaten</Header>}>
              <ColumnLayout columns={2} variant="text-grid">
                <FormField label="Name"><Input value={form.requesterName} onChange={({ detail }) => setField('requesterName', detail.value)} /></FormField>
                <FormField label="Unternehmen (optional)"><Input value={form.company} onChange={({ detail }) => setField('company', detail.value)} /></FormField>
                <FormField label="E-Mail"><Input type="email" value={form.email} onChange={({ detail }) => setField('email', detail.value)} /></FormField>
                <FormField label="Telefon (optional)"><Input value={form.phone} onChange={({ detail }) => setField('phone', detail.value)} /></FormField>
              </ColumnLayout>
              <div className="honeypot" aria-hidden="true"><label>Webseite<Input value={form.website} onChange={({ detail }) => setField('website', detail.value)} /></label></div>
              <Checkbox checked={form.privacyAccepted} onChange={({ detail }) => setField('privacyAccepted', detail.checked)}>
                Ich akzeptiere die Datenschutzhinweise und stimme der Weiterleitung meiner Bedarfsdaten an passende Vermieter zu.
              </Checkbox>
            </Container>
          </SpaceBetween>
        </form>
      </Form>
      <Modal
        visible={Boolean(detailMachine)}
        onDismiss={() => setDetailMachine(null)}
        header={detailMachine?.title || 'Maschinendetails'}
        size="large"
        footer={(
          <Box float="right">
            <Button
              variant="primary"
              href={resolveBackendAssetUrl(detailMachine?.datasheetUrl) || undefined}
              target={detailMachine?.datasheetUrl ? '_blank' : undefined}
              external={Boolean(detailMachine?.datasheetUrl)}
              disabled={!detailMachine?.datasheetUrl}
              disabledReason="Für diese Demo-Maschine ist noch kein Datenblatt hinterlegt."
            >
              Datenblatt ansehen
            </Button>
          </Box>
        )}
      >
        {detailMachine && (
          <div className="machine-detail-modal">
            <div className="machine-detail-hero">
              {detailMachine.imageUrl
                ? <img src={resolveBackendAssetUrl(detailMachine.imageUrl)} alt={detailMachine.title} />
                : <div className="request-machine-image-placeholder">SR</div>}
              <div>
                <span className="request-machine-path">
                  {detailMachine.category?.familyName} · {detailMachine.category?.name} · {detailMachine.model}
                </span>
                <h2>{detailMachine.manufacturer} {detailMachine.model}</h2>
                {detailMachine.description && <p>{detailMachine.description}</p>}
              </div>
            </div>
            <h3>Technische Daten</h3>
            <dl className="machine-detail-specs">
              {sortedSpecs(detailMachine).map((spec) => (
                <div key={spec.key}><dt>{spec.label}</dt><dd>{spec.value}{spec.unit ? ` ${spec.unit}` : ''}</dd></div>
              ))}
            </dl>
            {detailMachine.workDiagramUrl && (
              <div className="machine-work-diagram">
                <h3>Arbeitsdiagramm</h3>
                <img
                  src={resolveBackendAssetUrl(detailMachine.workDiagramUrl)}
                  alt={`Arbeitsdiagramm ${detailMachine.title}`}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
